'use client'

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  type ReactNode,
} from 'react'
import { fetchAppUserProgress } from '@/lib/app-data/user-progress'
import { useAuth } from '@/lib/auth/auth-context'
import { useUI } from '@/lib/ui-context'
import { mergeVhfCourseShellNav } from '@/lib/vhf-ui-nav'
import { REQUIRED_MODULES } from '@/lib/vhf-modules'

export type ProgressMap = Record<string, { quiz_passed: boolean | null }>

export interface CourseProgressState {
  progress: ProgressMap
  completed: number
  total: number
  percent: number
  nextIncompleteModuleId: string | null
  loading: boolean
  /** Set when the progress fetch failed; user can retry via mutate. */
  error: string | null
  mutate: () => void
}

const CourseProgressContext = createContext<CourseProgressState | null>(null)

function computeDerived(progress: ProgressMap) {
  const completed = REQUIRED_MODULES.filter((m) => progress[m.id]?.quiz_passed === true).length
  const total = REQUIRED_MODULES.length
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0
  const nextIncompleteModuleId =
    REQUIRED_MODULES.find((m) => progress[m.id]?.quiz_passed !== true)?.id ?? null
  return { completed, total, percent, nextIncompleteModuleId }
}

export function CourseProgressProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const ui = useUI()
  const [progress, setProgress] = useState<ProgressMap>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchProgress = useCallback(() => {
    if (!user) {
      setProgress({})
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)

    fetchAppUserProgress()
      .then((rows) => {
        const map: ProgressMap = {}
        rows.forEach((r) => {
          map[r.module_id] = { quiz_passed: r.quiz_passed }
        })
        setProgress(map)
        setLoading(false)
      })
      .catch((err: unknown) => {
        setLoading(false)
        const shellNav = mergeVhfCourseShellNav(ui)
        setError(shellNav.navProgressLoadError ?? 'Failed to load progress')
        if (typeof window !== 'undefined' && (window as Window & { __VERBOSE__?: boolean }).__VERBOSE__) {
          console.warn('CourseProgressProvider fetch error', err)
        }
      })
  }, [user, ui])

  useEffect(() => {
    queueMicrotask(() => fetchProgress())
  }, [fetchProgress])

  const value = useMemo(() => {
    const { completed, total, percent, nextIncompleteModuleId } = computeDerived(progress)
    return {
      progress,
      completed,
      total,
      percent,
      nextIncompleteModuleId,
      loading,
      error,
      mutate: fetchProgress,
    }
  }, [progress, loading, error, fetchProgress])

  return (
    <CourseProgressContext.Provider value={value}>
      {children}
    </CourseProgressContext.Provider>
  )
}

export function useCourseProgress(): CourseProgressState {
  const ctx = useContext(CourseProgressContext)
  if (!ctx) throw new Error('useCourseProgress must be used inside CourseProgressProvider')
  return ctx
}

export function useCourseProgressOptional(): CourseProgressState | null {
  return useContext(CourseProgressContext)
}
